import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "react-bootstrap-icons";
import { CSSTransition } from "react-transition-group";

import {
  Backdrop,
  CloseButton,
  Content,
  Counter,
  Image,
  ImageWrapper,
  NextButton,
  PrevButton,
  VideoPlayer,
} from "./styles";

interface LightboxImage {
  id: number;
  url: string;
  type: string;
}

interface LightboxProps {
  images: LightboxImage[];
  startIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

const TIMEOUT = 300;

export const Lightbox: React.FC<LightboxProps> = ({
  images,
  startIndex,
  isOpen,
  onClose,
}) => {
  const [index, setIndex] = useState(startIndex);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevStartIndex, setPrevStartIndex] = useState(startIndex);
  const backdropRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setIndex(startIndex);
  }
  if (isOpen && startIndex !== prevStartIndex) {
    setPrevStartIndex(startIndex);
    setIndex(startIndex);
  }

  useEffect(() => {
    if (!isOpen) return;
    if (images.length === 0) {
      onClose();
      return;
    }
    if (index >= images.length) setIndex(images.length - 1);
    else if (index < 0) setIndex(0);
  }, [isOpen, images.length, index, onClose]);

  const goPrev = useCallback(() => {
    setIndex((i) =>
      images.length === 0 ? 0 : (i - 1 + images.length) % images.length
    );
  }, [images.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (images.length === 0 ? 0 : (i + 1) % images.length));
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, goPrev, goNext, onClose]);

  const current = images[index];
  const hasMultiple = images.length > 1;

  return (
    <>
      <CSSTransition
        in={isOpen && !!current}
        timeout={TIMEOUT}
        classNames="fade"
        unmountOnExit
        nodeRef={backdropRef}
      >
        <Backdrop ref={backdropRef} className="fade" onClick={onClose} />
      </CSSTransition>
      <CSSTransition
        in={isOpen && !!current}
        timeout={TIMEOUT}
        classNames="fade"
        unmountOnExit
        nodeRef={contentRef}
      >
        <Content
          ref={contentRef}
          className="fade"
          role="dialog"
          aria-modal="true"
          aria-label="Media viewer"
          onClick={onClose}
        >
          <CloseButton
            ref={closeButtonRef}
            type="button"
            aria-label="Close image viewer"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            ×
          </CloseButton>
          {hasMultiple && (
            <Counter onClick={(e) => e.stopPropagation()}>
              {index + 1} / {images.length}
            </Counter>
          )}
          {hasMultiple && (
            <PrevButton
              type="button"
              aria-label="Previous media"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
            >
              <ChevronLeft size={24} />
            </PrevButton>
          )}
          <ImageWrapper onClick={(e) => e.stopPropagation()}>
            {current &&
              (current.type.startsWith("video") ? (
                <VideoPlayer
                  key={current.url}
                  src={current.url}
                  controls
                  autoPlay
                />
              ) : (
                <Image src={current.url} alt="" />
              ))}
          </ImageWrapper>
          {hasMultiple && (
            <NextButton
              type="button"
              aria-label="Next media"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
            >
              <ChevronRight size={24} />
            </NextButton>
          )}
        </Content>
      </CSSTransition>
    </>
  );
};
